import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { Routes, Route } from 'react-router-dom';
import { onAuthStateChanged } from 'firebase/auth';
import { doc, setDoc, onSnapshot, collection, updateDoc, increment, getDoc, getDocs, query, where } from 'firebase/firestore';
import { httpsCallable } from 'firebase/functions';
import { auth, db, functions, firebaseConfig } from './firebaseConfig';
import { AuthProvider, useAuth } from './AuthContext';
import { ThemeProvider, useTheme } from './ThemeContext';
import WelcomePage from './WelcomePage';
import CreatorProfile from './CreatorProfile';
import CreatorOnboarding from './CreatorOnboarding';
import WinnerShareCard from './WinnerShareCard';
import PublicWinnerPage from './PublicWinnerPage';
import PublicProfile from './PublicProfile';
import PlayerOnboardingFlow from './PlayerOnboardingFlow';
import CreatorOnboardingFlow from './CreatorOnboardingFlow';
import BlockBlast from './games/BlockBlast';
import ColorMatch from './games/ColorMatch';
import MemoryFlip from './games/MemoryFlip';
import WordGuess from './games/WordGuess';
import WhackAMole from './games/WhackAMole';

// --- CONFIGURATION SETUP ---
// NOTE: Global variables (__app_id, etc.) are used here for compatibility 
// with the Canvas environment. They are safely checked for existence.

// Fallback configuration for running the app outside the Canvas (e.g., local VS Code)
// Note: Firebase is now initialized in firebaseConfig.js

// Function to safely determine the appId
const getAppId = () => {
    // If running outside the Canvas, we MUST use the projectId 
    // as the default appId to ensure the Firestore path is correct 
    // for local development based on the security rules structure.

    // eslint-disable-next-line no-undef
    if (typeof __app_id !== 'undefined' && __app_id) {
        // eslint-disable-next-line no-undef
        return __app_id;
    }

    // *** NEW LOGIC: Use the projectId from the config as the local App ID ***
    return firebaseConfig.projectId;
}

// eslint-disable-next-line no-unused-vars
const appId = getAppId(); // Kept for potential future use

// --- UTILITY FUNCTIONS ---

// Cycle Management Utilities
const getCurrentCycleId = () => {
  // TESTING: 10-minute cycles instead of daily
  const now = new Date();
  const cstTime = new Date(now.toLocaleString('en-US', { timeZone: 'America/Chicago' }));
  
  // Round down to nearest 10-minute interval
  const currentMinutes = cstTime.getMinutes();
  const cycleMinute = Math.floor(currentMinutes / 10) * 10;
  cstTime.setMinutes(cycleMinute);
  cstTime.setSeconds(0);
  cstTime.setMilliseconds(0);
  
  const year = cstTime.getFullYear();
  const month = String(cstTime.getMonth() + 1).padStart(2, '0');
  const day = String(cstTime.getDate()).padStart(2, '0');
  const hour = String(cstTime.getHours()).padStart(2, '0');
  const minute = String(cstTime.getMinutes()).padStart(2, '0');
  
  return `${year}-${month}-${day}-${hour}:${minute}`;
};

// Helper functions for cycle-based structure
const getUserDocRef = (db, userId) => {
  return doc(db, 'users', userId);
};

const getCyclePickRef = (db, cycleId, userId) => {
  return doc(db, 'cycles', cycleId, 'picks', userId);
};

const getCycleLeaderboardRef = (db, cycleId, creatorId) => {
  return doc(db, 'cycles', cycleId, 'leaderboard', creatorId);
};

// --- MAIN REACT COMPONENT ---

const MainApp = () => {
  const { userProfile } = useAuth();
  const { isDarkMode } = useTheme();
  const [user, setUser] = useState(null);
  const [isAuthReady, setIsAuthReady] = useState(false);
  const [view, setView] = useState('minigame'); // 'minigame', 'leaderboard', 'creatorhub', 'creatorprofile'
  const prevViewRef = useRef('minigame'); // Track previous view for animation direction
  const onboardingTriggeredRef = useRef(false); // Track if onboarding triggered this session
  const [isTransitioning, setIsTransitioning] = useState(false);
  const [touchStart, setTouchStart] = useState(null);
  const [touchEnd, setTouchEnd] = useState(null);
  const [selectedGame, setSelectedGame] = useState(null); // null for library, 'reaction' or 'blockblast' for playing
  const [playerPoints, setPlayerPoints] = useState(0); // Points earned this cycle
  const [optimisticPoints, setOptimisticPoints] = useState(0); // Immediate UI update
  const [showPointsAnimation, setShowPointsAnimation] = useState(null); // Show +X animation
  const [creators, setCreators] = useState([]); // Leaderboard data
  const [selectedCreator, setSelectedCreator] = useState(null); // User's daily pick
  const [selectedCreatorProfile, setSelectedCreatorProfile] = useState(null); // Selected creator's full profile
  const [currentCycleId, setCurrentCycleId] = useState(getCurrentCycleId());
  const [reactionTestState, setReactionTestState] = useState('initial'); // 'initial', 'wait', 'go', 'result'
  const [reactionTime, setReactionTime] = useState(null);
  const [gameSessionId, setGameSessionId] = useState(null); // Store session ID
  const [gameStartTime, setGameStartTime] = useState(null); // Store start time
  const [profile, setProfile] = useState({ name: '', contentUrl: '' });
  const [profileStatus, setProfileStatus] = useState('');
  const [loading, setLoading] = useState(true);
  const [cycleWinner, setCycleWinner] = useState(null); // Yesterday's winner
  const [pityPoints, setPityPoints] = useState([]); // List of persistent pity points
  const [showCreatorOnboarding, setShowCreatorOnboarding] = useState(false); // Show onboarding modal
  const [showWinnerModal, setShowWinnerModal] = useState(false); // Show winner announcement modal
  const [showWinnerShareModal, setShowWinnerShareModal] = useState(false); // Show share modal for creators who won
  const [lastSeenWinner, setLastSeenWinner] = useState(null); // Track last seen winner to avoid re-showing
  const [timeRemaining, setTimeRemaining] = useState(''); // Countdown timer
  const [showOnboarding, setShowOnboarding] = useState(false); // Show onboarding flow
  const [creatorSearchQuery, setCreatorSearchQuery] = useState(''); // Search filter for creator hub
  const [onboardingType, setOnboardingType] = useState(null); // 'player' or 'creator'

  // COUNTDOWN TIMER - Update every second
  useEffect(() => {
    const updateCountdown = () => {
      const now = new Date();
      const cstTime = new Date(now.toLocaleString('en-US', { timeZone: 'America/Chicago' }));
      
      // Calculate next cycle (next 10-minute mark)
      const currentMinutes = cstTime.getMinutes();
      const nextCycleMinute = Math.ceil((currentMinutes + 1) / 10) * 10;
      const nextCycle = new Date(cstTime);
      
      if (nextCycleMinute >= 60) {
        nextCycle.setHours(nextCycle.getHours() + 1);
        nextCycle.setMinutes(0);
      } else {
        nextCycle.setMinutes(nextCycleMinute);
      }
      nextCycle.setSeconds(0);
      nextCycle.setMilliseconds(0);
      
      const diff = nextCycle - cstTime;
      
      if (diff > 0) {
        const hours = Math.floor(diff / (1000 * 60 * 60));
        const minutes = Math.floor((diff % (1000 * 60 * 60)) / (1000 * 60));
        const seconds = Math.floor((diff % (1000 * 60)) / 1000);
        
        const timeStr = `${String(hours).padStart(2, '0')}:${String(minutes).padStart(2, '0')}:${String(seconds).padStart(2, '0')}`;
        setTimeRemaining(timeStr);
      } else {
        setTimeRemaining('00:00:00');
      }
    };
    
    updateCountdown();
    const timer = setInterval(updateCountdown, 1000);
    
    return () => clearInterval(timer);
  }, []);

  // CYCLE CHANGE DETECTION - Check every minute if cycle has changed
  useEffect(() => {
    const checkCycleChange = () => {
      const newCycleId = getCurrentCycleId();
      if (newCycleId !== currentCycleId) {
        console.log('Cycle changed from', currentCycleId, 'to', newCycleId);
        setCurrentCycleId(newCycleId);
        // Reset points display for new cycle
        setPlayerPoints(0);
        setOptimisticPoints(0);
        setSelectedCreator(null);
      }
    };
    
    // Check immediately
    checkCycleChange();
    
    // Then check every minute
    const interval = setInterval(checkCycleChange, 60000);
    
    return () => clearInterval(interval);
  }, [currentCycleId]);

  // 1. AUTHENTICATION EFFECT
  useEffect(() => {
    let isMounted = true; // Flag to prevent state updates after unmount

    // NOTE: Removed automatic anonymous sign-in
    // The new auth flow requires users to choose their auth method on the welcome page first
    // Authentication is now handled by AuthContext (Google/Apple sign-in or guest mode)

    // Auth state change listener (Handles initial state and subsequent sign-outs)
    const unsubscribe = onAuthStateChanged(auth, async (currentUser) => {
      if (isMounted) {
        setUser(currentUser);
        setIsAuthReady(true);
        setLoading(false);
        
        if (currentUser && !currentUser.isAnonymous) {
          // Create/update user document in new structure
          const userDocRef = getUserDocRef(db, currentUser.uid);
          const userDoc = await getDoc(userDocRef);
          
          if (!userDoc.exists()) {
            // Create new user document
            await setDoc(userDocRef, {
              email: currentUser.email || '',
              displayName: currentUser.displayName || 'Anonymous Player',
              photoURL: currentUser.photoURL || '',
              accountType: 'player',
              createdAt: Date.now(),
              totalGamesPlayed: 0,
              totalPointsEarned: 0,
              currentStreak: 0,
              suspicionScore: 0,
              requiresCaptcha: false
            });
          }
        }
      }
    });

    return () => {
        isMounted = false; // Cleanup flag
        unsubscribe(); // Cleanup listener
    }
  }, []); // Removed isAuthReady to prevent infinite loop

  // Show onboarding for new users who haven't completed it
  useEffect(() => {
    if (user && isAuthReady && userProfile && !onboardingTriggeredRef.current) {
      onboardingTriggeredRef.current = true;
      // Only show if user hasn't completed onboarding
      if (!userProfile.onboardingComplete) {
        // Determine onboarding type based on user account
        if (userProfile.accountType === 'creator') {
          setOnboardingType('creator');
          setShowOnboarding(true);
        } else {
          setOnboardingType('player');
          setShowOnboarding(true);
        }
      }
    }
  }, [user, isAuthReady, userProfile]);

  // Check if creator needs onboarding (DISABLED IN TESTING MODE - using new onboarding flow)
  // useEffect(() => {
  //   if (userProfile && userProfile.accountType === 'creator' && !userProfile.creatorProfile?.profileComplete) {
  //     setShowCreatorOnboarding(true);
  //   } else {
  //     setShowCreatorOnboarding(false);
  //   }
  // }, [userProfile]);

  // 2. CYCLE PICK DATA LISTENER (User's current pick and points earned this cycle)
  useEffect(() => {
    // Skip if not authenticated
    if (!user || !isAuthReady) {
      setPlayerPoints(0);
      setSelectedCreator(null);
      return;
    }

    const cyclePickRef = getCyclePickRef(db, currentCycleId, user.uid);

    const unsubscribe = onSnapshot(cyclePickRef, (docSnap) => {
      if (docSnap.exists()) {
        const data = docSnap.data();
        const dbPoints = data.pointsEarned || 0;
        console.log('Pick document loaded:', data);
        console.log('Setting selectedCreator to:', data.creatorId);
        setPlayerPoints(dbPoints);
        // Sync optimistic points with database (in case of discrepancy)
        setOptimisticPoints(dbPoints);
        setSelectedCreator(data.creatorId || null);
      } else {
        // No pick yet for this cycle
        console.log('No pick document found for this cycle');
        setPlayerPoints(0);
        setOptimisticPoints(0);
        setSelectedCreator(null);
      }
    });

    return unsubscribe;
  }, [user, isAuthReady, currentCycleId]);

  // Fetch selected creator's profile
  useEffect(() => {
    if (!selectedCreator) {
      setSelectedCreatorProfile(null);
      return;
    }

    const fetchCreatorProfile = async () => {
      try {
        const creatorRef = getUserDocRef(db, selectedCreator);
        const creatorSnap = await getDoc(creatorRef);
        
        if (creatorSnap.exists()) {
          setSelectedCreatorProfile(creatorSnap.data());
        }
      } catch (error) {
        console.error('Error fetching creator profile:', error);
      }
    };

    fetchCreatorProfile();
  }, [selectedCreator]);

  // Onboarding callbacks (memoized to prevent unnecessary re-renders)
  const handleOnboardingComplete = useCallback(async () => {
    setShowOnboarding(false);
    // Mark onboarding as complete in Firestore
    if (user) {
      try {
        await updateDoc(doc(db, 'users', user.uid), {
          onboardingComplete: true
        });
      } catch (error) {
        console.error('Error marking onboarding complete:', error);
      }
    }
  }, [user]);

  const handleOnboardingSkip = useCallback(() => {
    setShowOnboarding(false);
  }, []);

  // 3. CREATOR/LEADERBOARD DATA LISTENER (Cycle-based)
  useEffect(() => {
    if (!db || !isAuthReady) return; // Wait until auth is ready

    const leaderboardRef = collection(db, 'cycles', currentCycleId, 'leaderboard');

    const unsubscribe = onSnapshot(leaderboardRef, async (snapshot) => {
      const creatorList = [];
      
      for (const docSnap of snapshot.docs) {
        const leaderboardData = docSnap.data();
        
        // Fetch creator profile from users collection
        const userRef = getUserDocRef(db, docSnap.id);
        const userSnap = await getDoc(userRef);
        
        const userData = userSnap.data();
        const isCreator = userData?.accountType === 'creator' || userData?.isCreator === true;
        
        if (userSnap.exists() && isCreator) {
          creatorList.push({
            id: docSnap.id,
            name: userData.displayName,
            contentUrl: userData.channelUrl || userData.promotionalURL || userData.creatorProfile?.channelUrl || '',
            points: leaderboardData.totalPoints || 0,
            supporterCount: leaderboardData.supporterCount || 0,
            photoURL: userData.photoURL || '',
            platform: userData.platform || userData.creatorProfile?.platform || '',
            tags: userData.tags || userData.creatorProfile?.tags || []
          });
        }
      }

      // Sort by points (highest first)
      creatorList.sort((a, b) => b.points - a.points);

      setCreators(creatorList);

      // Pre-fill profile if user is already a creator
      if (user && userProfile?.accountType === 'creator') {
        setProfile({ 
          name: userProfile.displayName, 
          contentUrl: userProfile.promotionalURL || '' 
        });
      }
    }, (error) => {
      console.error("Error fetching creators:", error);
    });

    return unsubscribe;
  }, [isAuthReady, currentCycleId, user, userProfile]); // Added dependencies

  // 4. LOAD YESTERDAY'S WINNER
  useEffect(() => {
    const loadWinner = async () => {
      if (!db || !isAuthReady) return;

      try {
        // Try to load current cycle's winner first
        const todayWinnerRef = doc(db, 'cycleWinners', currentCycleId);
        const todayWinnerSnap = await getDoc(todayWinnerRef);
        
        if (todayWinnerSnap.exists()) {
          setCycleWinner(todayWinnerSnap.data());
        } else {
          // If no winner in current cycle, check previous 10-minute cycle
          const prevCycleTime = new Date(new Date().toLocaleString('en-US', { timeZone: 'America/Chicago' }));
          prevCycleTime.setMinutes(prevCycleTime.getMinutes() - 10);
          const year = prevCycleTime.getFullYear();
          const month = String(prevCycleTime.getMonth() + 1).padStart(2, '0');
          const day = String(prevCycleTime.getDate()).padStart(2, '0');
          const hour = String(prevCycleTime.getHours()).padStart(2, '0');
          const minute = String(Math.floor(prevCycleTime.getMinutes() / 10) * 10).padStart(2, '0');
          const prevCycleId = `${year}-${month}-${day}-${hour}:${minute}`;
          
          const prevWinnerRef = doc(db, 'cycleWinners', prevCycleId);
          const prevWinnerSnap = await getDoc(prevWinnerRef);
          
          if (prevWinnerSnap.exists()) {
            setCycleWinner(prevWinnerSnap.data());
          }
        }
      } catch (error) {
        console.error('Error loading winner:', error);
      }
    };

    loadWinner();
  }, [isAuthReady, currentCycleId]);

  // Show winner announcement modal when new winner is detected
  useEffect(() => {
    if (cycleWinner && cycleWinner.cycleId !== lastSeenWinner) {
      // Check if current user is the winner and is a creator
      const isCurrentUserWinner = user && cycleWinner.winnerId === user.uid;
      const isCreator = userProfile?.accountType === 'creator';
      
      if (isCurrentUserWinner && isCreator) {
        // Show share modal for creators who won
        setShowWinnerShareModal(true);
      } else {
        // Show regular winner announcement for everyone else
        setShowWinnerModal(true);
      }
      setLastSeenWinner(cycleWinner.cycleId);
    }
  }, [cycleWinner, lastSeenWinner, user, userProfile]);

  // 5. CHECK PITY POINT ELIGIBILITY FROM PREVIOUS CYCLES
  useEffect(() => {
    const checkPityPointEligibility = async () => {
      if (!user || !isAuthReady || !db) {
        setPityPoints([]);
        return;
      }

      try {
        // Check last 10 cycles (100 minutes) for the most recent unclaimed pity point
        for (let cyclesAgo = 1; cyclesAgo <= 10; cyclesAgo++) {
          const checkCycleTime = new Date(new Date().toLocaleString('en-US', { timeZone: 'America/Chicago' }));
          checkCycleTime.setMinutes(checkCycleTime.getMinutes() - (cyclesAgo * 10));
          const year = checkCycleTime.getFullYear();
          const month = String(checkCycleTime.getMonth() + 1).padStart(2, '0');
          const day = String(checkCycleTime.getDate()).padStart(2, '0');
          const hour = String(checkCycleTime.getHours()).padStart(2, '0');
          const minute = String(Math.floor(checkCycleTime.getMinutes() / 10) * 10).padStart(2, '0');
          const checkCycleId = `${year}-${month}-${day}-${hour}:${minute}`;

          // Check if user has pity point eligibility in this cycle
          const eligibilityRef = doc(db, 'cycles', checkCycleId, 'pityPointsEligible', user.uid);
          const eligibilitySnap = await getDoc(eligibilityRef);

          if (eligibilitySnap.exists()) {
            const eligibilityData = eligibilitySnap.data();
            console.log(`Cycle ${checkCycleId} eligibility data:`, JSON.stringify(eligibilityData));
            
            // Only show if not claimed yet
            if (!eligibilityData.claimed && eligibilityData.eligibleForPityPoint) {
              const creatorId = eligibilityData.creatorId || eligibilityData.theirCreatorId;
              
              if (!creatorId) {
                console.warn('No creatorId in eligibility data:', eligibilityData);
                continue;
              }
              
              try {
                // Fetch creator's display name
                const creatorRef = doc(db, 'users', creatorId);
                const creatorSnap = await getDoc(creatorRef);
                const creatorData = creatorSnap.exists() ? creatorSnap.data() : null;
                const creatorName = (creatorData && creatorData.displayName) ? 
                  creatorData.displayName : 
                  'Unknown Creator';
                
                // Found the most recent unclaimed pity point - set it and stop looking
                setPityPoints([{
                  creatorId,
                  creatorName,
                  cyclesLost: 1,
                  fromCycleId: checkCycleId
                }]);
                console.log(`User has pity point available for creator: ${creatorName} from cycle ${checkCycleId}`);
                return; // Exit early - only show the most recent one
              } catch (error) {
                console.error('Error fetching creator name:', error);
              }
            }
          }
        }
        
        // No unclaimed pity points found
        setPityPoints([]);
        console.log('No unclaimed pity points found in last 10 cycles');
      } catch (error) {
        console.error('Error checking pity point eligibility:', error);
        setPityPoints([]);
      }
    };

    checkPityPointEligibility();
    
    // Re-check every 30 seconds
    const interval = setInterval(checkPityPointEligibility, 30000);

    return () => clearInterval(interval);
  }, [user, isAuthReady, currentCycleId, db]);

  // Helper function for optimistic updates and animations
  const showPointsEarned = useCallback((points) => {
    // Immediately update UI
    setOptimisticPoints(prev => prev + points);
    // Show animation
    setShowPointsAnimation(points);
    // Hide animation after 3 seconds
    setTimeout(() => setShowPointsAnimation(null), 3000);
  }, []);

  // Show animation only (for games where database listener handles the points)
  const showPointsAnimationOnly = useCallback((points) => {
    setShowPointsAnimation(points);
    setTimeout(() => setShowPointsAnimation(null), 3000);
  }, []);

  // --- MINIGAME LOGIC (Reaction Test) ---

  // Reset reaction test when leaving the game
  useEffect(() => {
    if (selectedGame !== 'reaction') {
      setReactionTestState('initial');
      setReactionTime(null);
      setGameSessionId(null);
      setGameStartTime(null);
    }
  }, [selectedGame]);

  // Update previous view ref for animations
  useEffect(() => {
    prevViewRef.current = view;
    // Reset transition flag after animation completes
    if (isTransitioning) {
      const timer = setTimeout(() => setIsTransitioning(false), 200);
      return () => clearTimeout(timer);
    }
  }, [view, isTransitioning]);

  const startReactionTest = useCallback(() => {
    if (!user) {
      setProfileStatus('Please sign in to play!');
      return;
    }
    
    // Start the visual game immediately - no delay
    setReactionTestState('wait');
    setReactionTime(null);
    const delay = Math.random() * 3000 + 2000; // 2 to 5 seconds
    setTimeout(() => {
      setReactionTestState('go');
      // Start tracking time from this moment
      setGameStartTime(Date.now());
    }, delay);
    
    // Create session in the background (don't await)
    const startGameSession = httpsCallable(functions, 'startGameSession');
    startGameSession({
      gameType: 'reaction',
      difficulty: 'standard'
    }).then(result => {
      const sessionId = result.data.sessionId;
      // Store session ID in state
      setGameSessionId(sessionId);
    }).catch(error => {
      console.error('Error starting game session:', error.message);
      // Don't show error to user - they're already playing
    });
  }, [user, selectedCreator]);

  const handleReactionClick = useCallback(async () => {
    if (view !== 'minigame') return;
    if (reactionTestState === 'wait') {
      // Too early click
      setReactionTestState('result');
      setReactionTime('Too Early!');
      return;
    }

    if (reactionTestState === 'go') {
      // Successful click!
      const timeElapsed = Date.now() - gameStartTime;
      setReactionTime(`${timeElapsed} ms`);
      setReactionTestState('result');

      if (timeElapsed < 500) {
        if (!user || !gameSessionId) {
          setProfileStatus('Error: Game session not started properly');
          return;
        }

        // Check if creator is selected
        if (!selectedCreator) {
          setProfileStatus('🎉 Nice! Pick a creator from the Creator page to start earning points');
          return;
        }

        // Show points immediately (optimistic update)
        showPointsEarned(1);

        try {
          // Call Cloud Function to validate and record the result
          const submitGameResult = httpsCallable(functions, 'submitGameResult');
          const result = await submitGameResult({
            sessionId: gameSessionId,
            timeTaken: timeElapsed
          });
          
          if (result.data.success) {
            setProfileStatus(`Success! +${result.data.pointsAwarded} points for ${creators.find(c => c.id === result.data.creatorId)?.name || 'creator'}!`);
          } else if (!result.data.success) {
            setProfileStatus('Error: Failed to submit game result');
            // Rollback optimistic update on error
            setOptimisticPoints(prev => prev - 1);
          }
        } catch (error) {
          console.error('Error submitting game result:', error.message);
          setProfileStatus(`Error submitting result: ${error.message}`);
          // Rollback optimistic update on error
          setOptimisticPoints(prev => prev - 1);
        }
      }
      return;
    }

    if (reactionTestState === 'initial' || reactionTestState === 'result') {
      startReactionTest();
    }
  }, [reactionTestState, user, view, startReactionTest, selectedCreator, gameSessionId, gameStartTime, creators, showPointsEarned]);


  // Handle Block Blast game win
  const handleBlockBlastWin = useCallback(async (sessionId) => {
    if (!user) {
      console.log('Cannot award points - user not logged in');
      return;
    }

    if (!selectedCreator) {
      alert('🎉 Great job! Pick a creator from the Creator page to start earning points');
      return;
    }

    if (!sessionId) {
      console.error('No session ID provided');
      return;
    }

    // Show points immediately (optimistic update)
    showPointsEarned(5);

    try {
      console.log('🎮 Block Blast Win! Submitting result...');

      // Submit the game result
      const submitGameResult = httpsCallable(functions, 'submitGameResult');
      const result = await submitGameResult({
        sessionId,
        timeTaken: 0
      });

      console.log('Result submitted:', result.data);

      if (result.data.success) {
        setProfileStatus(`Success! +5 points for ${creators.find(c => c.id === selectedCreator)?.name || 'creator'}!`);
      } else {
        setProfileStatus('Error: Failed to submit game result');
        // Rollback optimistic update on error
        setOptimisticPoints(prev => prev - 5);
      }
    } catch (error) {
      console.error('Error submitting Block Blast result:', error);
      setProfileStatus(`Error: ${error.message}`);
      // Rollback optimistic update on error
      setOptimisticPoints(prev => prev - 5);
    }
  }, [user, selectedCreator, creators, showPointsEarned]);

  // Handle Color Match game win
  const handleColorMatchWin = useCallback(async (sessionId) => {
    if (!user) {
      console.log('Cannot award points - user not logged in');
      return;
    }

    if (!selectedCreator) {
      alert('🎉 Amazing! Pick a creator from the Creator page to start earning points');
      return;
    }

    if (!sessionId) {
      console.error('No session ID provided');
      return;
    }

    // Show points immediately (optimistic update)
    showPointsEarned(8);

    try {
      console.log('🎨 Color Match Win! Submitting result...');

      // Submit the game result
      const submitGameResult = httpsCallable(functions, 'submitGameResult');
      const result = await submitGameResult({
        sessionId,
        timeTaken: 0
      });

      console.log('Result submitted:', result.data);

      if (result.data.success) {
        setProfileStatus(`Success! +8 points for ${creators.find(c => c.id === selectedCreator)?.name || 'creator'}!`);
      } else {
        setProfileStatus('Error: Failed to submit game result');
        // Rollback optimistic update on error
        setOptimisticPoints(prev => prev - 8);
      }
    } catch (error) {
      console.error('Error submitting Color Match result:', error);
      setProfileStatus(`Error: ${error.message}`);
      // Rollback optimistic update on error
      setOptimisticPoints(prev => prev - 8);
    }
  }, [user, selectedCreator, creators, showPointsEarned]);

  // Handle game start - create session
  const handleGameStart = useCallback(async (gameType) => {
    if (!user) {
      console.log('Cannot start game - user not logged in');
      return null;
    }

    if (!selectedCreator) {
      return null; // Will show alert on win if no creator selected
    }

    try {
      console.log(`� Starting ${gameType} game - creating session...`);
      const startGameSession = httpsCallable(functions, 'startGameSession');
      const sessionResult = await startGameSession({
        gameType,
        difficulty: 'standard'
      });
      console.log('Session created:', sessionResult.data.sessionId);
      return sessionResult.data.sessionId;
    } catch (error) {
      console.error('Error creating game session:', error);
      return null;
    }
  }, [user, selectedCreator]);

  // Handle Memory Flip game win
  const handleMemoryFlipWin = useCallback(async (sessionId) => {
    if (!user) {
      console.log('Cannot award points - user not logged in');
      return;
    }

    if (!selectedCreator) {
      alert('🎉 Excellent memory! Pick a creator from the Creator page to start earning points');
      return;
    }

    if (!sessionId) {
      console.error('No session ID provided');
      return;
    }

    // Show points immediately (optimistic update)
    showPointsEarned(6);

    try {
      console.log('🃏 Memory Flip Win! Submitting result...');

      // Submit the game result
      const submitGameResult = httpsCallable(functions, 'submitGameResult');
      const result = await submitGameResult({
        sessionId,
        timeTaken: 0
      });

      console.log('Result submitted:', result.data);

      if (result.data.success) {
        setProfileStatus(`Success! +6 points for ${creators.find(c => c.id === selectedCreator)?.name || 'creator'}!`);
      } else {
        setProfileStatus('Error: Failed to submit game result');
        // Rollback optimistic update on error
        setOptimisticPoints(prev => prev - 6);
      }
    } catch (error) {
      console.error('Error submitting Memory Flip result:', error);
      setProfileStatus(`Error: ${error.message}`);
      // Rollback optimistic update on error
      setOptimisticPoints(prev => prev - 6);
    }
  }, [user, selectedCreator, creators, showPointsEarned]);

  // Handle Word Guess game win
  const handleWordGuessComplete = useCallback(async (sessionId) => {
    if (!user) {
      console.log('Cannot award points - user not logged in');
      return;
    }

    if (!selectedCreator) {
      alert('🎉 Great guessing! Pick a creator from the Creator page to start earning points');
      return;
    }

    if (!sessionId) {
      console.error('No session ID provided');
      return;
    }

    try {
      const submitResult = httpsCallable(functions, 'submitGameResult');
      const result = await submitResult({
        sessionId,
        score: 100,
        duration: 0
      });

      console.log('Word Guess result submitted:', result.data);

      if (result.data.pointsAwarded) {
        const pointsEarned = result.data.pointsAwarded;
        console.log(`🎉 ${pointsEarned} points awarded!`);
        
        showPointsAnimationOnly(pointsEarned);
        // Don't use optimistic increment - the snapshot listener will update it from the database
        // This prevents double-counting or accumulation bugs
        // setOptimisticPoints(prev => prev + pointsEarned);
      }
    } catch (error) {
      console.error('Error submitting Word Guess result:', error);
      // Don't roll back optimistic points - let the snapshot listener handle it
      // setOptimisticPoints(prev => prev - 15);
    }
  }, [user, selectedCreator, creators, showPointsAnimationOnly]);

  // Legacy handler kept for compatibility
  const handlePatternProWin = handleWordGuessComplete;

  // Handle Whack-a-Mole game win
  const handleWhackAMoleWin = useCallback(async (sessionId) => {
    if (!user) {
      console.log('Cannot award points - user not logged in');
      return;
    }

    if (!selectedCreator) {
      alert('🎉 Quick reflexes! Pick a creator from the Creator page to start earning points');
      return;
    }

    if (!sessionId) {
      console.error('No session ID provided');
      return;
    }

    // Show points immediately (optimistic update)
    showPointsEarned(3);

    try {
      console.log('🎪 Whack-a-Mole Win! Submitting result...');

      // Submit the game result
      const submitGameResult = httpsCallable(functions, 'submitGameResult');
      const result = await submitGameResult({
        sessionId,
        timeTaken: 0
      });

      console.log('Result submitted:', result.data);

      if (result.data.success) {
        setProfileStatus(`Success! +3 points for ${creators.find(c => c.id === selectedCreator)?.name || 'creator'}!`);
      } else {
        setProfileStatus('Error: Failed to submit game result');
        // Rollback optimistic update on error
        setOptimisticPoints(prev => prev - 3);
      }
    } catch (error) {
      console.error('Error submitting Whack-a-Mole result:', error);
      setProfileStatus(`Error: ${error.message}`);
      // Rollback optimistic update on error
      setOptimisticPoints(prev => prev - 3);
    }
  }, [user, selectedCreator, creators, showPointsEarned]);

  const miniGameContent = useMemo(() => {
    // If a game is selected, show the game
    if (selectedGame === 'blockblast') {
      return (
        <div className="space-y-4">
          <button
            onClick={() => setSelectedGame(null)}
            className="flex items-center gap-2 px-4 py-2 bg-gray-700 hover:bg-gray-600 text-white rounded-lg transition-colors"
          >
            ← Back to Games
          </button>
          <BlockBlast onGameWin={handleBlockBlastWin} onGameStart={handleGameStart} />
        </div>
      );
    }

    if (selectedGame === 'colormatch') {
      return (
        <div className="space-y-4">
          <button
            onClick={() => setSelectedGame(null)}
            className="flex items-center gap-2 px-4 py-2 bg-gray-700 hover:bg-gray-600 text-white rounded-lg transition-colors"
          >
            ← Back to Games
          </button>
          <ColorMatch onGameWin={handleColorMatchWin} onGameStart={handleGameStart} />
        </div>
      );
    }

    if (selectedGame === 'memoryflip') {
      return (
        <div className="space-y-4">
          <button
            onClick={() => setSelectedGame(null)}
            className="flex items-center gap-2 px-4 py-2 bg-gray-700 hover:bg-gray-600 text-white rounded-lg transition-colors"
          >
            ← Back to Games
          </button>
          <MemoryFlip onGameWin={handleMemoryFlipWin} onGameStart={handleGameStart} />
        </div>
      );
    }

    if (selectedGame === 'wordguess') {
      return (
        <div className="space-y-4">
          <button
            onClick={() => setSelectedGame(null)}
            className="flex items-center gap-2 px-4 py-2 bg-gray-700 hover:bg-gray-600 text-white rounded-lg transition-colors"
          >
            ← Back to Games
          </button>
          <WordGuess onGameWin={handleWordGuessComplete} onGameStart={handleGameStart} isDarkMode={isDarkMode} />
        </div>
      );
    }

    if (selectedGame === 'whackamole') {
      return (
        <div className="space-y-4">
          <button
            onClick={() => setSelectedGame(null)}
            className="flex items-center gap-2 px-4 py-2 bg-gray-700 hover:bg-gray-600 text-white rounded-lg transition-colors"
          >
            ← Back to Games
          </button>
          <WhackAMole onGameWin={handleWhackAMoleWin} onGameStart={handleGameStart} />
        </div>
      );
    }

    if (selectedGame === 'reaction') {
      const timeValue = reactionTime && reactionTime !== 'Too Early!' ? parseInt(reactionTime) : Infinity;
      const isWin = timeValue < 500;

      let message;
      let boxClass = 'cursor-pointer w-full h-80 flex items-center justify-center rounded-2xl shadow-xl transition-all duration-300 text-3xl font-bold';

      switch (reactionTestState) {
        case 'initial':
          boxClass += ' bg-gradient-to-br from-purple-600 to-purple-700 hover:from-purple-500 hover:to-purple-600 text-white';
          message = 'Click to Start Reaction Test';
          break;
        case 'wait':
          boxClass += ' bg-red-500 text-white';
          message = 'Wait for Green...';
          break;
        case 'go':
          boxClass += ' bg-green-500 text-white shadow-2xl shadow-green-400';
          message = 'CLICK NOW!';
          break;
        case 'result':
          boxClass += ' bg-gray-800 text-gray-100 border-2 border-gray-700';
          message = (
            <div className="text-center p-4">
              <p className="text-5xl mb-4">{reactionTime}</p>
              {reactionTime === 'Too Early!' && <p className="text-red-400">You lost this round: Too Early!</p>}
              {isWin && <p className="text-yellow-400">Success! You earned 1 Sub Point!</p>}
              {!isWin && reactionTime !== 'Too Early!' && reactionTime !== null && <p className="text-orange-400">Too slow. Try again!</p>}
              <button
                onClick={startReactionTest}
                className="mt-6 px-6 py-2 bg-gradient-to-r from-purple-600 to-purple-700 text-white rounded-full hover:from-purple-500 hover:to-purple-600 transition-all shadow-md"
              >
                Play Again
              </button>
            </div>
          );
          break;
        default:
          message = 'Error';
      }

      return (
        <div className="space-y-4">
          <button
            onClick={() => setSelectedGame(null)}
            className="flex items-center gap-2 px-4 py-2 bg-gray-700 hover:bg-gray-600 text-white rounded-lg transition-colors"
          >
            ← Back to Games
          </button>
          <div id="reaction-box" className={boxClass} onClick={handleReactionClick}>
            {message}
          </div>
        </div>
      );
    }

    // Game Library View (default)
    const games = [
      {
        id: 'reaction',
        name: 'Reaction Test',
        iconSvg: (isDarkMode) => (
          <div className="relative w-full h-full overflow-hidden">
            <img src="/images/raction-test.png" alt="Reaction Test" className="w-full h-full object-cover" />
            <div className={`absolute inset-0 backdrop-blur-[2px] ${isDarkMode ? 'bg-[#0F1535]/60' : 'bg-white/60'}`}></div>
          </div>
        ),
        description: 'Test your reflexes! Click as fast as you can when the screen turns green.',
        color: 'from-purple-600 to-purple-700',
        points: 1,
      },
      {
        id: 'whackamole',
        name: 'Whack-a-Mole',
        iconSvg: (isDarkMode) => (
          <div className="relative w-full h-full overflow-hidden">
            <img src="/images/whack-a-mole.png" alt="Whack-a-Mole" className="w-full h-full object-cover" />
            <div className={`absolute inset-0 backdrop-blur-[2px] ${isDarkMode ? 'bg-[#0F1535]/60' : 'bg-white/60'}`}></div>
          </div>
        ),
        description: 'Tap the critters before they hide! Hit 30 in 30 seconds to win.',
        color: 'from-orange-500 to-red-600',
        points: 3,
      },
      {
        id: 'blockblast',
        name: 'Block Blast',
        iconSvg: (isDarkMode) => (
          <div className="relative w-full h-full overflow-hidden">
            <img src="/images/Block-blast.png" alt="Block Blast" className="w-full h-full object-cover" />
            <div className={`absolute inset-0 backdrop-blur-[2px] ${isDarkMode ? 'bg-[#0F1535]/60' : 'bg-white/60'}`}></div>
          </div>
        ),
        description: 'Clear lines by placing blocks strategically. Reach 100 points to win!',
        color: 'from-yellow-500 to-yellow-600',
        points: 5,
      },
      {
        id: 'memoryflip',
        name: 'Memory Flip',
        iconSvg: (isDarkMode) => (
          <div className="relative w-full h-full overflow-hidden">
            <img src="/images/memory-flip.png" alt="Memory Flip" className="w-full h-full object-cover" />
            <div className={`absolute inset-0 backdrop-blur-[2px] ${isDarkMode ? 'bg-[#0F1535]/60' : 'bg-white/60'}`}></div>
          </div>
        ),
        description: 'Match all the pairs! Find 8 matching card pairs to win.',
        color: 'from-purple-500 to-pink-600',
        points: 6,
      },
      {
        id: 'colormatch',
        name: 'Color Match',
        iconSvg: (isDarkMode) => (
          <div className="relative w-full h-full overflow-hidden">
            <img src="/images/color-match.png" alt="Color Match" className="w-full h-full object-cover" />
            <div className={`absolute inset-0 backdrop-blur-[2px] ${isDarkMode ? 'bg-[#0F1535]/60' : 'bg-white/60'}`}></div>
          </div>
        ),
        description: 'Simon says! Repeat the color sequence. Reach level 8 to win!',
        color: 'from-blue-500 to-blue-600',
        points: 8,
      },
      {
        id: 'wordguess',
        name: 'Word Guess',
        iconSvg: (isDarkMode) => (
          <div className="relative w-full h-full overflow-hidden">
            <img src="/images/word-guess.png" alt="Word Guess" className="w-full h-full object-cover" />
            <div className={`absolute inset-0 backdrop-blur-[2px] ${isDarkMode ? 'bg-[#0F1535]/60' : 'bg-white/60'}`}></div>
          </div>
        ),
        description: 'Guess the 5-letter word in 6 tries - like Wordle!',
        color: 'from-purple-500 to-blue-600',
        points: 15,
      },
    ];

    return (
      <div className="space-y-6">
        {!selectedCreator && (
          <div className="bg-blue-500/20 backdrop-blur-sm border border-blue-400/30 rounded-lg p-4 text-center">
            <p className={`text-sm md:text-base ${isDarkMode ? 'text-gray-200' : 'text-gray-800'}`}>
              <strong>Reminder:</strong> Pick a creator from the Creator page to make your points count!
            </p>
            <button
              onClick={() => handleViewChange('creatorhub')}
              className="mt-3 bg-gradient-to-r from-purple-600 to-pink-600 hover:from-purple-700 hover:to-pink-700 text-white font-semibold py-2 px-4 rounded-lg transition text-sm"
            >
              Choose a Creator
            </button>
          </div>
        )}

        <div className="grid grid-cols-3 md:grid-cols-4 gap-2 md:gap-3">
          {games.map((game) => {
            return (
              <button
                key={game.id}
                onClick={() => setSelectedGame(game.id)}
                className={`group relative aspect-square ${isDarkMode ? 'bg-[#0F1535] hover:bg-[#1A1F3E] border-white/10 hover:border-white/20' : 'bg-white hover:bg-blue-50 border-gray-200 hover:border-blue-200'} rounded-lg overflow-hidden hover:scale-[1.02] transition-all duration-300 shadow-lg border`}
              >
                {/* Game image as background */}
                <div className="absolute inset-0">
                  {game.iconSvg(isDarkMode)}
                </div>
                
                {/* Shimmer overlay - only visible on hover in dark mode */}
                {isDarkMode && (
                  <div 
                    className="absolute inset-0 opacity-0 group-hover:opacity-100 transition-opacity duration-300 pointer-events-none z-10"
                    style={{
                      background: 'linear-gradient(135deg, rgba(96, 165, 250, 0.1) 0%, rgba(147, 197, 253, 0.2) 25%, rgba(96, 165, 250, 0.1) 50%, rgba(147, 197, 253, 0.2) 75%, rgba(96, 165, 250, 0.1) 100%)',
                      backgroundSize: '200% 200%',
                      animation: 'shimmer 2s ease-in-out infinite'
                    }}
                  />
                )}
                
                {/* Text overlay at bottom */}
                <div className="absolute bottom-0 left-0 right-0 p-2 md:p-3 z-20 flex flex-col gap-1 items-center">
                  <h3 className={`text-[11px] leading-tight md:text-sm font-bold ${isDarkMode ? 'text-white' : 'text-white'} drop-shadow-lg line-clamp-2 text-center`}>
                    {game.name}
                  </h3>
                  
                  <span className={`px-2 py-0.5 md:px-2.5 md:py-1 rounded-full bg-gradient-to-r ${game.color} text-white text-[10px] md:text-xs font-semibold shadow-lg`}>
                    {game.points} pts
                  </span>
                </div>
              </button>
            );
          })}
        </div>
      </div>
    );
  }, [selectedGame, reactionTestState, reactionTime, startReactionTest, handleReactionClick, handleBlockBlastWin, handleColorMatchWin, handleMemoryFlipWin, handlePatternProWin, handleWhackAMoleWin, handleGameStart, selectedCreator, userProfile]);

  // --- CREATOR HUB LOGIC ---

  const [selectedCreatorForModal, setSelectedCreatorForModal] = useState(null);
  const [allCreators, setAllCreators] = useState([]);

  // Fetch all creators for Creator Hub
  useEffect(() => {
    const fetchAllCreators = async () => {
      try {
        const usersRef = collection(db, 'users');
        const creatorsSnapshot = await getDocs(usersRef);
        
        const creatorsList = [];
        creatorsSnapshot.forEach((doc) => {
          const data = doc.data();
          const isCreator = data.accountType === 'creator' || data.isCreator === true;
          if (isCreator && data.creatorProfile?.profileComplete) {
            creatorsList.push({
              id: doc.id,
              name: data.displayName || 'Unknown Creator',
              photoURL: data.photoURL || '',
              channelUrl: data.creatorProfile?.channelUrl || '',
              promotionalUrl: data.creatorProfile?.promotionalUrl || '',
              platform: data.creatorProfile?.platform || 'Other',
              contentType: data.creatorProfile?.contentType || 'Other',
            });
          }
        });
        
        setAllCreators(creatorsList);
      } catch (error) {
        console.error('Error fetching creators:', error);
      }
    };

    if (view === 'creatorhub') {
      fetchAllCreators();
    }
  }, [view]);

  const handleSupportCreator = async (creatorId) => {
    if (!user) {
      alert('Please sign in to support a creator');
      return;
    }

    // Prevent switching if already supporting a creator
    if (selectedCreator && selectedCreator !== creatorId) {
      alert('You are already supporting a creator for this cycle. Your choice is locked until the next cycle.');
      return;
    }

    try {
      console.log('Supporting creator:', creatorId);
      
      const cyclePickRef = getCyclePickRef(db, currentCycleId, user.uid);
      await setDoc(cyclePickRef, {
        creatorId: creatorId,
        pickedAt: Date.now()
      });

      console.log('Cycle pick saved successfully');

      // Track unique supporter if this is their first time supporting this creator
      const creatorRef = doc(db, 'users', creatorId);
      const supporterTrackingRef = doc(db, 'users', creatorId, 'supporters', user.uid);
      
      console.log('Checking if supporter exists...');
      const supporterDoc = await getDoc(supporterTrackingRef);
      
      if (!supporterDoc.exists()) {
        // First time supporting this creator
        console.log('First time supporter - creating tracking doc and incrementing uniqueSupporters');
        await setDoc(supporterTrackingRef, {
          userId: user.uid,
          firstSupported: Date.now(),
          lastSupported: Date.now()
        });
        
        // Check if creator document exists before updating
        const creatorDoc = await getDoc(creatorRef);
        if (creatorDoc.exists()) {
          await updateDoc(creatorRef, {
            uniqueSupporters: increment(1)
          });
          console.log('Updated uniqueSupporters count');
        } else {
          console.warn('Creator document does not exist, skipping uniqueSupporters update');
        }
      } else {
        // Update last supported time
        console.log('Updating last supported time');
        await updateDoc(supporterTrackingRef, {
          lastSupported: Date.now()
        });
      }

      setSelectedCreator(creatorId);
      setSelectedCreatorForModal(null);
      alert('Creator supported! You can now play games and earn points for them.');
      handleViewChange('minigame'); // Redirect to games
    } catch (error) {
      console.error('Error supporting creator:', error);
      console.error('Error details:', error.message, error.code);
      alert('Error supporting creator. Please try again.');
    }
  };

  const getPlatformIcon = (platform, size = 'w-6 h-6') => {
    const icons = {
      'YouTube': '/images/youtube-icon.png',
      'Instagram': '/images/instagram-icon.png', 
      'Twitch': '/images/twitch-icon.png',
      'TikTok': '/images/tiktok-icon.png',
      'Other': null
    };
    
    const iconUrl = icons[platform];
    
    if (!iconUrl) {
      return <span className="text-2xl">🌐</span>;
    }
    
    return <img src={iconUrl} alt={`${platform} icon`} className={`${size} object-contain`} />;
  };

  // Filter creators for creator hub
  const filteredHubCreators = useMemo(() => {
    if (!creatorSearchQuery.trim()) return allCreators;
    
    const query = creatorSearchQuery.toLowerCase();
    return allCreators.filter(creator => {
      // Search by creator name
      if (creator.name?.toLowerCase().includes(query)) return true;
      
      // Search by platform
      if (creator.platform?.toLowerCase().includes(query)) return true;
      
      // Search by content tags/genre
      if (creator.tags && Array.isArray(creator.tags)) {
        if (creator.tags.some(tag => tag.toLowerCase().includes(query))) return true;
      }
      
      return false;
    });
  }, [allCreators, creatorSearchQuery]);

  const creatorHubContent = (
    <div className="space-y-6">
      {/* Header with title and search */}
      <div className="text-center space-y-4">
        <h2 className={`text-2xl md:text-3xl font-extrabold ${isDarkMode ? 'text-white' : 'text-black'}`}>Choose Your Creator</h2>
        <p className={`text-sm ${isDarkMode ? 'text-gray-400' : 'text-gray-600'}`}>Support your favorite creator by playing games and earning them points!</p>
        
        {/* Search Bar */}
        <div className="max-w-md mx-auto">
          <input
            type="text"
            placeholder="Search creators, platforms, or genres..."
            value={creatorSearchQuery}
            onChange={(e) => setCreatorSearchQuery(e.target.value)}
            className={`w-full px-4 py-2 rounded-lg border transition-colors ${
              isDarkMode 
                ? 'bg-white/10 border-white/20 text-white placeholder-gray-400 focus:border-purple-500'
                : 'bg-white border-gray-300 text-gray-900 placeholder-gray-500 focus:border-purple-500'
            } focus:outline-none focus:ring-2 focus:ring-purple-500/50`}
          />
        </div>
      </div>
      
      {allCreators.length === 0 ? (
        <div className="text-center py-12">
          <div className="text-6xl mb-4">🔍</div>
          <p className="text-gray-700 text-lg">No creators found yet</p>
          <p className="text-gray-600 text-sm mt-2">Be the first to sign up as a creator!</p>
        </div>
      ) : filteredHubCreators.length === 0 ? (
        <div className="text-center py-12">
          <div className="text-6xl mb-4">🔍</div>
          <p className={`text-lg ${isDarkMode ? 'text-gray-300' : 'text-gray-700'}`}>No creators found matching "{creatorSearchQuery}"</p>
          <p className={`text-sm mt-2 ${isDarkMode ? 'text-gray-400' : 'text-gray-600'}`}>Try a different search term</p>
        </div>
      ) : (
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
          {filteredHubCreators.map((creator) => (
            <button
              key={creator.id}
              onClick={() => setSelectedCreatorForModal(creator)}
              className="hover:scale-105 transition-all duration-300"
            >
              <div className="flex flex-col items-center text-center">
                <div className="relative mb-1">
                  {creator.photoURL ? (
                    <img 
                      src={creator.photoURL} 
                      alt={creator.name}
                      className="w-20 h-20 md:w-24 md:h-24 rounded-full border-2 border-white/30 hover:border-white/60 transition-all"
                    />
                  ) : (
                    <div className="w-20 h-20 md:w-24 md:h-24 rounded-full bg-gradient-to-br from-purple-500 to-pink-500 flex items-center justify-center text-3xl md:text-4xl border-2 border-white/30 hover:border-white/60 transition-all">
                      👤
                    </div>
                  )}
                  
                  {/* Platform badge - positioned bottom right */}
                  <div className="absolute bottom-0 right-0 bg-white rounded-full shadow-lg border border-gray-200">
                    {getPlatformIcon(creator.platform, 'w-8 h-8 md:w-9 md:h-9')}
                  </div>
                </div>
                <p className={`text-xs md:text-sm font-semibold truncate w-full px-1 ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>
                  {creator.name}
                </p>
              </div>
            </button>
          ))}
        </div>
      )}
    </div>
  );

  // --- LEADERBOARD LOGIC ---

  const handlePickCreator = useCallback(async (creatorId) => {
    if (!user) {
      setProfileStatus("Please sign in to pick a creator.");
      return;
    }

    try {
      const pickRef = getCyclePickRef(db, currentCycleId, user.uid);
      const pickSnap = await getDoc(pickRef);
      
      if (pickSnap.exists()) {
        // Pick already exists for this cycle - just update UI state
        setSelectedCreator(creatorId);
        setProfileStatus(`You've already picked a creator for this cycle. Locked until cycle ends!`);
        return;
      }
      
      // Create new pick
      await setDoc(pickRef, {
        userId: user.uid,
        creatorId: creatorId,
        pointsEarned: 0,
        pickedAt: Date.now(),
        lastSwitchedAt: Date.now(),
        switchCount: 0
      });
      
      // Add user to creator's supporter list
      const leaderboardRef = getCycleLeaderboardRef(db, currentCycleId, creatorId);
      const leaderboardSnap = await getDoc(leaderboardRef);
      
      if (leaderboardSnap.exists()) {
        const supporters = leaderboardSnap.data().supporters || [];
        if (!supporters.includes(user.uid)) {
          await updateDoc(leaderboardRef, {
            supporterCount: increment(1),
            supporters: [...supporters, user.uid]
          });
        }
      }
      
      setSelectedCreator(creatorId);
      setProfileStatus(`Now supporting ${creators.find(c => c.id === creatorId)?.name || 'creator'}! Start playing to earn points for them.`);
    } catch (error) {
      console.error('Error picking creator:', error);
      setProfileStatus(`Error: ${error.message}`);
    }
  }, [user, creators, currentCycleId]);

  const leaderboardContent = (
    <div className="p-2 md:p-4 space-y-4 md:space-y-6">
      <div className="text-center space-y-4">
        <h2 className={`text-xl md:text-3xl font-extrabold ${isDarkMode ? 'text-white' : 'text-black'} pb-2`}>Creator Leaderboard</h2>
        
        {/* Countdown Timer */}
        <div className="inline-block">
          <p className={`text-xs md:text-sm mb-2 ${isDarkMode ? 'text-gray-400' : 'text-gray-600'}`}>Next Winner In</p>
          <div 
            className="relative px-6 md:px-8 py-3 md:py-4 rounded-2xl font-mono text-2xl md:text-4xl font-bold"
            style={{
              background: isDarkMode ? '#1a1a2e' : '#2a2a3e',
              color: '#e0e0e0',
              boxShadow: isDarkMode 
                ? '0 0 40px rgba(102, 126, 234, 0.6), 0 0 80px rgba(236, 72, 153, 0.4), inset 0 0 20px rgba(0, 0, 0, 0.3)'
                : '0 0 40px rgba(102, 126, 234, 0.5), 0 0 80px rgba(236, 72, 153, 0.3), inset 0 0 20px rgba(0, 0, 0, 0.3)',
            }}
          >
            {timeRemaining || '00:00:00'}
          </div>
        </div>
      </div>

      {creators.length === 0 ? (
        <p className={`text-sm md:text-base ${isDarkMode ? 'text-gray-400' : 'text-gray-600'}`}>No creators registered yet. Be the first!</p>
      ) : (
        <div className={`${isDarkMode ? 'bg-white/5 border-white/20 backdrop-blur-xl' : 'bg-gray-50 border-gray-200'} rounded-xl shadow-lg p-3 md:p-6 border`}>
          <ul className="space-y-2 md:space-y-3">
            {creators.map((creator, index) => {
              const isFirstPlace = index === 0;
              return (
                <li
                  key={creator.id}
                  className={`flex items-center justify-between p-3 md:p-5 rounded-lg shadow-sm transition-all border ${
                    isFirstPlace 
                      ? `border-4 border-yellow-400 ${isDarkMode ? 'bg-gradient-to-r from-yellow-500/20 to-orange-500/20 backdrop-blur-xl' : 'bg-gradient-to-r from-yellow-50 to-orange-50'} shadow-lg shadow-yellow-500/20`
                      : `${isDarkMode ? 'bg-white/5 hover:bg-white/10 border-white/20 backdrop-blur-xl' : 'bg-white hover:bg-gray-50 border-gray-200'}`
                  }`}
                >
                  <div className="flex items-center flex-1 min-w-0">
                    {/* Crown icon for first place */}
                    {isFirstPlace && (
                      <div className="mr-2 md:mr-3 flex-shrink-0">
                        <svg className="w-6 h-6 md:w-8 md:h-8 text-yellow-400" fill="currentColor" viewBox="0 0 20 20">
                          <path d="M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.07 3.292a1 1 0 00.95.69h3.462c.969 0 1.371 1.24.588 1.81l-2.8 2.034a1 1 0 00-.364 1.118l1.07 3.292c.3.921-.755 1.688-1.54 1.118l-2.8-2.034a1 1 0 00-1.175 0l-2.8 2.034c-.784.57-1.838-.197-1.539-1.118l1.07-3.292a1 1 0 00-.364-1.118L2.98 8.72c-.783-.57-.38-1.81.588-1.81h3.461a1 1 0 00.951-.69l1.07-3.292z" />
                        </svg>
                      </div>
                    )}
                    
                    {/* Profile Picture */}
                    {creator.photoURL ? (
                      <img 
                        src={creator.photoURL} 
                        alt={creator.name}
                        className={`w-10 h-10 md:w-12 md:h-12 rounded-full mr-2 md:mr-4 flex-shrink-0 object-cover ${
                          isFirstPlace ? 'border-4 border-yellow-400' : 'border-2 border-yellow-400/70'
                        }`}
                        onError={(e) => {
                          e.target.style.display = 'none';
                          e.target.nextElementSibling.style.display = 'flex';
                        }}
                      />
                    ) : null}
                    <div 
                      className="w-10 h-10 md:w-12 md:h-12 rounded-full mr-2 md:mr-4 bg-gray-200 flex items-center justify-center text-gray-700 text-lg md:text-xl font-bold flex-shrink-0 border border-gray-300"
                      style={{ display: creator.photoURL ? 'none' : 'flex' }}
                    >
                      {creator.name.charAt(0).toUpperCase()}
                    </div>
                    
                    {/* Creator Info */}
                    <div className="flex-1 min-w-0">
                      <p className={`text-sm md:text-lg font-medium truncate ${
                        isFirstPlace 
                          ? `font-bold ${isDarkMode ? 'text-yellow-300' : 'text-yellow-700'}`
                          : `${isDarkMode ? 'text-white' : 'text-black'}`
                      }`}>
                        {index + 1}. {creator.name}
                      </p>
                      <a 
                        href={creator.contentUrl} 
                        target="_blank" 
                        rel="noopener noreferrer" 
                        className="inline-block mt-1 px-3 py-1 text-white text-xs md:text-sm rounded-lg transition-colors"
                        style={{ backgroundColor: '#00a6fb' }}
                        onMouseEnter={(e) => e.target.style.backgroundColor = '#0095e0'}
                        onMouseLeave={(e) => e.target.style.backgroundColor = '#00a6fb'}
                      >
                        Visit Channel
                      </a>
                      <p className={`text-xs ${isDarkMode ? 'text-gray-400' : 'text-gray-600'} mt-1`}>
                        {creator.supporterCount || 0} supporter{creator.supporterCount !== 1 ? 's' : ''}
                      </p>
                    </div>
                  </div>
                  
                  {/* Points */}
                  <div className="ml-2 md:ml-4">
                    <span className={`text-lg md:text-2xl font-extrabold ${
                      isFirstPlace 
                        ? 'text-yellow-400'
                        : `${isDarkMode ? 'text-yellow-400' : 'text-black'}`
                    }`}>{creator.points || 0}</span>
                  </div>
                </li>
              );
            })}
          </ul>
        </div>
      )}
    </div>
  );

  // --- RENDERING ---

  if (loading) {
    return <div className={`min-h-screen flex items-center justify-center ${isDarkMode ? '' : ''}`} style={!isDarkMode ? {backgroundColor: '#f5f6ff'} : {}}><div className={`text-xl font-medium ${isDarkMode ? 'text-gray-100' : 'text-gray-900'}`}>Loading The Subgames...</div></div>;
  }

  // Handle view changes with animation
  const viewOrder = ['minigame', 'leaderboard', 'creatorhub', 'creatorprofile'];
  
  const handleViewChange = (newView) => {
    if (newView === view) return; // No change
    setIsTransitioning(true);
    setView(newView);
  };

  // Swipe handling for touch navigation
  const minSwipeDistance = 50;

  const onTouchStart = (e) => {
    // Disable swipe navigation when playing a game
    if (selectedGame) return;
    
    setTouchEnd(null);
    setTouchStart(e.targetTouches[0].clientX);
  };

  const onTouchMove = (e) => {
    // Disable swipe navigation when playing a game
    if (selectedGame) return;
    
    setTouchEnd(e.targetTouches[0].clientX);
  };

  const onTouchEnd = () => {
    // Disable swipe navigation when playing a game
    if (selectedGame) return;
    
    if (!touchStart || !touchEnd) return;
    
    const distance = touchStart - touchEnd;
    const isLeftSwipe = distance > minSwipeDistance;
    const isRightSwipe = distance < -minSwipeDistance;

    const viewOrder = ['minigame', 'leaderboard', 'creatorhub', 'creatorprofile'];
    const currentIndex = viewOrder.indexOf(view);

    if (isLeftSwipe && currentIndex < viewOrder.length - 1) {
      // Swipe left = move to next tab
      handleViewChange(viewOrder[currentIndex + 1]);
    }
    
    if (isRightSwipe && currentIndex > 0) {
      // Swipe right = move to previous tab
      handleViewChange(viewOrder[currentIndex - 1]);
    }
  };

  const currentViewContent = () => {
    switch (view) {
      case 'minigame':
        return miniGameContent;
      case 'leaderboard':
        return leaderboardContent;
      case 'creatorhub':
        return creatorHubContent;
      case 'creatorprofile':
        return <CreatorProfile />;
      default:
        return <div>Select a view.</div>;
    }
  };

  const handleClaimPityPoint = async (creatorId, creatorName) => {
    console.log('Claiming pity point for creatorId:', creatorId);
    
    if (!cycleWinner || !cycleWinner.promotionalURL) {
      console.error('No cycle winner or promotional URL available');
      alert('Winner link not available. Cannot claim pity point.');
      return;
    }

    try {
      console.log(`Claiming pity point for creator: ${creatorId} (${creatorName})`);
      console.log('Opening winner URL:', cycleWinner.promotionalURL);

      // Call cloud function to claim pity point
      const clickWinnerLink = httpsCallable(functions, 'clickWinnerLink');
      const result = await clickWinnerLink({
        creatorId: creatorId,
        winnerId: cycleWinner.winnerId
      });

      console.log('clickWinnerLink result:', result.data);

      if (result.data.success && result.data.pityPointApplied) {
        alert(`Pity point claimed! +${result.data.pointsAwarded} point added to ${creatorName}!`);
        
        // Refresh pity points to remove claimed one
        const updatedPityPoints = pityPoints.filter(p => p.creatorId !== creatorId);
        setPityPoints(updatedPityPoints);
      }

      // Open the winner's promotional URL
      window.open(cycleWinner.promotionalURL, '_blank');
    } catch (error) {
      console.error('Error claiming pity point:', error);
      console.error('Error details:', error.message);
      alert('Error processing pity point. Please try again.');
    }
  };

  return (
    <div className={`min-h-screen ${isDarkMode ? '' : ''} flex flex-col font-inter`} style={!isDarkMode ? {backgroundColor: '#f5f6ff'} : {}}>
      {/* Header/Navbar with animated arrow mosaic */}
      <header 
        className={`p-2 md:p-4 sticky top-0 backdrop-blur-xl ${isDarkMode ? 'bg-transparent' : ''}`}
        style={{
          position: 'relative',
          overflow: 'hidden',
          zIndex: 50,
          background: isDarkMode ? 'transparent' : 'radial-gradient(ellipse at bottom, #1B254B 0%, #0B1437 100%)'
        }}
      >
        <div className="max-w-4xl mx-auto flex justify-between items-center relative z-10">
          <h1 className="text-2xl md:text-4xl font-black text-white flex items-baseline" style={{textShadow: '0 0 2px black, 0 0 2px black, 0 0 2px black, 0 0 2px black'}}>
            <span className="text-sm md:text-xl font-normal mr-0.5 md:mr-1">The</span>
            <span style={{letterSpacing: '-0.02em'}}>SubGames</span>
          </h1>
          <div className="flex items-center space-x-2 md:space-x-4">
            <div className="relative flex items-center gap-2">
              {/* Points Animation - on the left */}
              {showPointsAnimation && (
                <div 
                  className="text-2xl md:text-3xl font-bold text-green-400 pointer-events-none"
                  style={{
                    animation: 'floatUp 3s ease-out forwards'
                  }}
                >
                  +{showPointsAnimation}
                </div>
              )}
              <span className="text-lg md:text-xl font-bold text-white flex items-center gap-1 md:gap-2">
                <span className="bg-white rounded-full w-7 h-7 md:w-8 md:h-8 flex items-center justify-center flex-shrink-0">
                  <img src="/green-arrow.png" alt="points" className="w-5 h-5 md:w-6 md:h-6" />
                </span>
                <span>{optimisticPoints}</span>
              </span>
            </div>
            {selectedCreator && (
              <span className="text-xs md:text-sm text-white bg-black/70 backdrop-blur-sm px-2 py-1 md:p-2 rounded-lg truncate max-w-[100px] md:max-w-none">
                <span className="hidden md:inline">Supporting: </span>{selectedCreatorProfile?.displayName || 'Creator'}
              </span>
            )}
          </div>
        </div>
      </header>

      {/* Main Content Area */}
      <main className="flex-1 max-w-4xl mx-auto w-full p-2 md:p-4">
        {/* Winner Announcement */}
        {cycleWinner && (
          <div 
            className={`winner-banner-glitter ${isDarkMode ? 'border-2 border-yellow-400/40 backdrop-blur-xl shadow-2xl' : 'bg-gradient-to-r from-yellow-500 to-yellow-600'} text-white p-3 md:p-4 rounded-xl mb-4 md:mb-6 relative overflow-hidden`}
            style={isDarkMode ? {
              background: 'linear-gradient(135deg, rgba(234, 179, 8, 0.15) 0%, rgba(251, 191, 36, 0.25) 25%, rgba(245, 158, 11, 0.15) 50%, rgba(251, 191, 36, 0.25) 75%, rgba(234, 179, 8, 0.15) 100%)',
              backgroundSize: '200% 200%',
              animation: 'shimmer 3s ease-in-out infinite'
            } : {}}
          >
            {isDarkMode && (
              <div 
                className="absolute inset-0 opacity-30"
                style={{
                  backgroundImage: `
                    radial-gradient(circle at 20% 50%, rgba(251, 191, 36, 0.4) 0%, transparent 50%),
                    radial-gradient(circle at 80% 50%, rgba(245, 158, 11, 0.4) 0%, transparent 50%),
                    radial-gradient(circle at 50% 80%, rgba(234, 179, 8, 0.3) 0%, transparent 50%)
                  `
                }}
              />
            )}
            <div className="flex items-center justify-between gap-4 md:gap-6 mb-3">
              {cycleWinner.winnerPhotoURL && (
                <img 
                  src={cycleWinner.winnerPhotoURL} 
                  alt={cycleWinner.winnerName}
                  className="w-16 h-16 md:w-20 md:h-20 rounded-full border-4 border-white shadow-lg object-cover flex-shrink-0"
                />
              )}
              <div className="flex-1 flex flex-col items-center justify-center text-center">
                <p className="font-extrabold text-base md:text-lg mb-1">
                  Winner: {cycleWinner.winnerName}
                </p>
                <p className="text-xs md:text-sm opacity-90">
                  {cycleWinner.finalScore} points • {cycleWinner.supporterCount} supporters
                </p>
              </div>
              {/* Invisible spacer for balance */}
              <div className="w-16 md:w-20 flex-shrink-0"></div>
            </div>
            
            {cycleWinner.promotionalURL && selectedCreator === cycleWinner.winnerId ? (
              <div className="text-center">
                <p className="text-sm md:text-base font-semibold mb-2">
                  🎉 You picked the winner! Check out their content!
                </p>
                <button
                  onClick={() => window.open(cycleWinner.promotionalURL, '_blank')}
                  className="bg-white text-yellow-600 px-4 py-2 md:px-6 md:py-3 rounded-lg font-bold hover:bg-gray-100 transition-colors text-sm md:text-base"
                >
                  Visit Winner's Channel
                </button>
              </div>
            ) : cycleWinner.promotionalURL ? (
              <div className="text-center">
                <p className="text-xs md:text-sm opacity-90">
                  We support each other!
                </p>
                <button
                  onClick={() => window.open(cycleWinner.promotionalURL, '_blank')}
                  className="mt-2 bg-white text-yellow-600 px-4 py-2 md:px-6 md:py-3 rounded-lg font-bold hover:bg-gray-100 transition-colors text-sm md:text-base"
                >
                  Visit Channel
                </button>
              </div>
            ) : null}
          </div>
        )}

        {/* Dynamic Content */}
        <div 
          className="p-3 md:p-6 min-h-[500px] overflow-hidden relative"
          onTouchStart={onTouchStart}
          onTouchMove={onTouchMove}
          onTouchEnd={onTouchEnd}
        >
          <div className="relative w-full h-full">
            <div 
              className={`w-full ${isTransitioning ? 'transition-transform duration-200 ease-out' : ''}`}
              style={{ 
                transform: `translateX(${-viewOrder.indexOf(view) * 100}%)`,
                willChange: 'transform'
              }}
            >
              <div className="flex w-full">
                {viewOrder.map((v) => (
                  <div 
                    key={v}
                    className="w-full flex-shrink-0"
                    style={{ width: '100%' }}
                  >
                    {v === view && currentViewContent()}
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </main>

      {/* Footer/Navigation */}
      <footer className={`${isDarkMode ? 'bg-transparent border-t border-white/10' : ''} p-3 md:p-4 sticky bottom-0 w-full backdrop-blur-md`} style={!isDarkMode ? {backgroundColor: '#f5f6ff'} : {}}>
        <div className="max-w-4xl mx-auto flex justify-around items-center">
          <NavItem view="minigame" currentView={view} icon={(
            <i className="fa-solid fa-gamepad"></i>
          )} label="Games" onViewChange={handleViewChange} />
          <NavItem view="leaderboard" currentView={view} icon={(
            <i className="fa-solid fa-trophy"></i>
          )} label="Leaderboard" onViewChange={handleViewChange} />
          <NavItem view="creatorhub" currentView={view} icon={(
            <i className="fa-solid fa-users"></i>
          )} label="Creators" onViewChange={handleViewChange} />
          <NavItem view="creatorprofile" currentView={view} icon={(
            <i className="fa-solid fa-user"></i>
          )} label="My Profile" onViewChange={handleViewChange} />
        </div>
      </footer>

      {/* Creator Onboarding Modal */}
      {showCreatorOnboarding && (
        <CreatorOnboarding 
          onComplete={() => setShowCreatorOnboarding(false)}
        />
      )}

      {/* Creator Detail Modal */}
      {selectedCreatorForModal && (
        <div className="fixed inset-0 bg-black/70 backdrop-blur-sm flex items-center justify-center z-50 p-4" onClick={() => setSelectedCreatorForModal(null)}>
          <div className="bg-gradient-to-br from-[#3B5998] to-[#2A4475] rounded-2xl p-6 max-w-md w-full border-2 border-white/20 shadow-2xl" onClick={(e) => e.stopPropagation()}>
            <button 
              onClick={() => setSelectedCreatorForModal(null)}
              className="float-right text-white/70 hover:text-white text-2xl"
            >
              ×
            </button>
            
            <div className="text-center mb-6">
              {selectedCreatorForModal.photoURL ? (
                <img 
                  src={selectedCreatorForModal.photoURL} 
                  alt={selectedCreatorForModal.name}
                  className="w-24 h-24 rounded-full border-4 border-white/30 mx-auto mb-4"
                />
              ) : (
                <div className="w-24 h-24 rounded-full bg-gradient-to-br from-purple-500 to-pink-500 flex items-center justify-center text-4xl border-4 border-white/30 mx-auto mb-4">
                  👤
                </div>
              )}
              
              <h2 className="text-2xl font-bold text-white mb-2">{selectedCreatorForModal.name}</h2>
              
              <div className="flex items-center justify-center gap-2 mb-4">
                {getPlatformIcon(selectedCreatorForModal.platform, 'w-8 h-8')}
                <span className="text-white/90">{selectedCreatorForModal.platform}</span>
              </div>

              {selectedCreatorForModal.contentType && (
                <div className="text-sm text-white/80 bg-white/10 px-3 py-1 rounded-full inline-block mb-4">
                  {selectedCreatorForModal.contentType} Content
                </div>
              )}
            </div>

            <div className="space-y-3">
              {selectedCreator === selectedCreatorForModal.id ? (
                <div className="bg-green-500/20 border border-green-500/50 text-green-200 px-4 py-3 rounded-lg text-center font-semibold">
                  ✓ Currently Supporting
                </div>
              ) : (
                <button
                  onClick={() => handleSupportCreator(selectedCreatorForModal.id)}
                  className="w-full bg-gradient-to-r from-purple-600 to-pink-600 hover:from-purple-700 hover:to-pink-700 text-white font-bold py-3 px-6 rounded-lg transition-all"
                >
                  Support This Creator
                </button>
              )}
              
              <button
                onClick={() => {
                  setSelectedCreatorForModal(null);
                  window.location.href = `/profile/${selectedCreatorForModal.id}`;
                }}
                className="w-full bg-white/10 hover:bg-white/20 text-white font-semibold py-3 px-6 rounded-lg transition-all border border-white/20"
              >
                View Profile
              </button>

              {selectedCreatorForModal.channelUrl && (
                <button
                  onClick={() => window.open(selectedCreatorForModal.channelUrl, '_blank')}
                  className="w-full bg-white/10 hover:bg-white/20 text-white font-semibold py-3 px-6 rounded-lg transition-all border border-white/20 flex items-center justify-center gap-2"
                >
                  {getPlatformIcon(selectedCreatorForModal.platform, 'w-5 h-5')}
                  <span>Visit Channel</span>
                </button>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Winner Share Modal - For creators who won */}
      {showWinnerShareModal && cycleWinner && (
        <WinnerShareCard
          winner={cycleWinner}
          cycleId={cycleWinner.cycleId}
          onClose={() => setShowWinnerShareModal(false)}
        />
      )}

      {/* Winner Announcement Modal */}
      {showWinnerModal && cycleWinner && (
        <div className="fixed inset-0 bg-black/70 backdrop-blur-sm flex items-center justify-center z-50 p-4">
          <div className="bg-gradient-to-br from-yellow-500 via-orange-500 to-red-500 rounded-2xl p-6 max-w-md w-full border-4 border-yellow-300/50 shadow-2xl animate-pulse-slow">
            <button 
              onClick={() => setShowWinnerModal(false)}
              className="float-right text-white/70 hover:text-white text-2xl"
            >
              ×
            </button>
            
            <div className="text-center mb-6">
              <h2 className="text-3xl font-bold text-white mb-2">Cycle Winner!</h2>
              <p className="text-white/90 text-sm">The competition has ended</p>
            </div>

            <div className="bg-white/20 backdrop-blur rounded-xl p-4 mb-6">
              {cycleWinner.winnerPhotoURL && (
                <img 
                  src={cycleWinner.winnerPhotoURL} 
                  alt={cycleWinner.winnerName}
                  className="w-24 h-24 rounded-full border-4 border-white/50 mx-auto mb-4"
                />
              )}
              
              <h3 className="text-2xl font-bold text-white text-center mb-2">
                {cycleWinner.winnerName}
              </h3>
              
              <div className="text-center space-y-1">
                <div className="text-white/90">
                  <span className="font-bold text-xl">{cycleWinner.finalScore}</span> points
                </div>
                <div className="text-white/80 text-sm">
                  {cycleWinner.supporterCount} supporters
                </div>
              </div>
            </div>

            <div className="space-y-3">
              {/* Pity Points Section - Show if user has pity points available */}
              {pityPoints && pityPoints.length > 0 && (
                <div className="bg-white/30 backdrop-blur rounded-xl p-4 mb-2">
                  <p className="text-white font-semibold text-center mb-1">
                    Check out this creators content for a bonus point!
                  </p>
                  <p className="text-white/90 text-sm text-center mb-3">
                    Support them if enjoy their content!
                  </p>
                  <div className="space-y-2">
                    {pityPoints.map((pityPoint) => (
                      <div key={pityPoint.creatorId} className="bg-white/40 p-3 rounded-lg">
                        <p className="text-sm text-white/90 mb-2 text-center">
                          <strong>{pityPoint.creatorName}</strong> - {pityPoint.cyclesLost} cycle{pityPoint.cyclesLost > 1 ? 's' : ''} lost
                        </p>
                        <button
                          onClick={() => {
                            handleClaimPityPoint(pityPoint.creatorId, pityPoint.creatorName);
                            setShowWinnerModal(false);
                          }}
                          className="w-full bg-white text-orange-600 px-3 py-2 rounded-lg font-bold hover:bg-gray-100 transition-colors text-sm border border-orange-400"
                        >
                          Visit Winner
                        </button>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {cycleWinner.promotionalURL && (
                <button
                  onClick={() => {
                    window.open(cycleWinner.promotionalURL, '_blank');
                    setShowWinnerModal(false);
                  }}
                  className="w-full bg-white text-orange-600 hover:bg-gray-100 font-bold py-3 px-6 rounded-lg transition-all shadow-lg"
                >
                  🔗 Check Out Winner's Links
                </button>
              )}
              
              <button
                onClick={() => setShowWinnerModal(false)}
                className="w-full bg-white/20 hover:bg-white/30 text-white font-semibold py-3 px-6 rounded-lg transition-all border border-white/30"
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Onboarding Flow - Testing Mode (shows every sign-in) */}
      {showOnboarding && onboardingType === 'player' && (
        <PlayerOnboardingFlow
          onComplete={handleOnboardingComplete}
          onSkip={handleOnboardingSkip}
          creators={creators}
        />
      )}

      {showOnboarding && onboardingType === 'creator' && (
        <CreatorOnboardingFlow
          onComplete={handleOnboardingComplete}
          onSkip={handleOnboardingSkip}
        />
      )}
    </div>
  );
};

// --- NAVIGATION COMPONENT ---

const NavItem = ({ view, currentView, onViewChange, icon, label }) => {
  const { isDarkMode } = useTheme();
  const isActive = view === currentView;
  return (
    <button
      onClick={() => onViewChange(view)}
      className={`flex flex-col items-center p-1 md:p-2 rounded-xl transition-all ${
        isActive
          ? (isDarkMode ? 'text-white font-semibold' : 'text-black font-semibold')
          : (isDarkMode ? 'text-gray-400 hover:text-gray-200' : 'text-gray-500 hover:text-gray-700')
      }`}
    >
      <div className="w-6 h-6 md:w-7 md:h-7">{icon}</div>
      <span className="text-[10px] md:text-xs font-medium mt-0.5 md:mt-1">{label}</span>
    </button>
  );
};

// --- MAIN APP WITH AUTHENTICATION ROUTING ---

const App = () => {
  const [hasCompletedWelcome, setHasCompletedWelcome] = useState(false);

  return (
    <AuthProvider>
      <ThemeProvider>
        <Routes>
          <Route path="/winner/:cycleId" element={<PublicWinnerPage />} />
          <Route path="/profile/:userId" element={<PublicProfile />} />
          <Route path="/privacy-policy.html" element={
            <iframe 
              src="/privacy-policy.html" 
              style={{ width: '100%', height: '100vh', border: 'none' }}
              title="Privacy Policy"
            />
          } />
          <Route path="/terms-of-service.html" element={
            <iframe 
              src="/terms-of-service.html" 
              style={{ width: '100%', height: '100vh', border: 'none' }}
              title="Terms of Service"
            />
          } />
          <Route path="*" element={
            <AppContent 
              hasCompletedWelcome={hasCompletedWelcome}
              setHasCompletedWelcome={setHasCompletedWelcome}
            />
          } />
        </Routes>
      </ThemeProvider>
    </AuthProvider>
  );
};

const AppContent = ({ hasCompletedWelcome, setHasCompletedWelcome }) => {
  const { currentUser, loading } = useAuth();
  const [isOAuthCallback, setIsOAuthCallback] = useState(false);
  const [oauthStatus, setOAuthStatus] = useState('');

  // Handle OAuth callback on mount - BEFORE any other logic
  useEffect(() => {
    const handleOAuthCallback = async () => {
      const urlParams = new URLSearchParams(window.location.search);
      const code = urlParams.get('code');
      const state = urlParams.get('state');
      const error = urlParams.get('error');

      console.log('OAuth callback check:', { code: !!code, state: !!state, error });

      if (code || error) {
        setIsOAuthCallback(true);
        
        if (error) {
          console.error('OAuth error from provider:', error);
          setOAuthStatus(`OAuth error: ${error}`);
          setTimeout(() => {
            window.location.href = '/';
          }, 3000);
          return;
        }

        try {
          console.log('Starting OAuth authentication process...');
          setOAuthStatus('Authenticating...');

          // Determine platform from stored state
          const twitchState = sessionStorage.getItem('twitch_oauth_state');
          const tiktokState = sessionStorage.getItem('tiktok_oauth_state');

          console.log('Stored states:', { twitchState, tiktokState, receivedState: state });

          let platform = null;
          if (state === twitchState) {
            platform = 'twitch';
            sessionStorage.removeItem('twitch_oauth_state');
          } else if (state === tiktokState) {
            platform = 'tiktok';
            sessionStorage.removeItem('tiktok_oauth_state');
          } else {
            throw new Error('Invalid state - possible security issue');
          }

          console.log('Detected platform:', platform);
          setOAuthStatus(`Signing in with ${platform}...`);

          // Call Cloud Function
          console.log('About to call Cloud Function with:', { code, platform, redirectUri: window.location.origin });
          const handleOAuthCode = httpsCallable(functions, 'handleOAuthCallback');
          console.log('Created callable function');
          const result = await handleOAuthCode({
            code,
            platform,
            redirectUri: window.location.origin
          });

          console.log('Cloud function result:', result.data);

          if (!result.data.success) {
            throw new Error(result.data.error || 'Authentication failed');
          }

          const { customToken } = result.data;

          // Sign in with custom token
          const { signInWithCustomToken } = await import('firebase/auth');
          await signInWithCustomToken(auth, customToken);

          console.log('Successfully signed in with custom token');
          setOAuthStatus('Success! Redirecting...');
          
          // Clear URL params and redirect
          setTimeout(() => {
            window.history.replaceState({}, document.title, '/');
            window.location.reload();
          }, 1000);

        } catch (error) {
          console.error('OAuth callback error:', error);
          setOAuthStatus(`Error: ${error.message}`);
          setTimeout(() => {
            window.location.href = '/';
          }, 3000);
        }
      }
    };

    handleOAuthCallback();
  }, []);

  // Reset welcome screen when user signs out
  useEffect(() => {
    if (!loading && !currentUser) {
      setHasCompletedWelcome(false);
    }
  }, [currentUser, loading, setHasCompletedWelcome]);

  // Show loading state while auth is initializing
  if (loading) {
    return (
      <div className="min-h-screen bg-neutral-50 flex items-center justify-center">
        <div className="text-gray-900 text-2xl">Loading...</div>
      </div>
    );
  }

  // Show OAuth callback screen if processing
  if (isOAuthCallback) {
    return (
      <div style={{
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        minHeight: '100vh',
        background: 'linear-gradient(to bottom right, #0F1535, #1a1a2e, #16213e)',
        color: 'white',
        fontFamily: 'system-ui, -apple-system, sans-serif'
      }}>
        <div style={{
          background: 'rgba(255, 255, 255, 0.1)',
          backdropFilter: 'blur(10px)',
          borderRadius: '16px',
          padding: '40px',
          maxWidth: '400px',
          textAlign: 'center'
        }}>
          <h1 style={{ fontSize: '24px', marginBottom: '20px' }}>
            🔐 Authenticating
          </h1>
          
          <p style={{ fontSize: '18px', marginBottom: '20px' }}>
            {oauthStatus || 'Processing...'}
          </p>

          <div style={{ marginTop: '20px' }}>
            <div style={{
              border: '3px solid rgba(255, 255, 255, 0.1)',
              borderTop: '3px solid white',
              borderRadius: '50%',
              width: '40px',
              height: '40px',
              animation: 'spin 1s linear infinite',
              margin: '0 auto'
            }}></div>
          </div>
        </div>

        <style>{`
          @keyframes spin {
            0% { transform: rotate(0deg); }
            100% { transform: rotate(360deg); }
          }
        `}</style>
      </div>
    );
  }

  // Show welcome page if user hasn't signed in yet
  if (!hasCompletedWelcome && !currentUser) {
    return <WelcomePage onContinue={() => setHasCompletedWelcome(true)} />;
  }

  // Show main app once authenticated
  return <MainApp />;
};

export default App;

